import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
} from '../ui/drawer';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';

/**
 * Bottom-sheet action drawer for mobile rows (invoices, bills, contacts…).
 *
 * Props:
 *   open: boolean
 *   onClose: () => void
 *   title: string (row label, e.g. "INV-1042 · Acme Co.")
 *   subtitle?: string (status / amount line)
 *   actions: Array<{
 *     icon: lucide component,
 *     label: string,
 *     onClick: () => void,
 *     variant?: 'default' | 'destructive' | 'ghost',
 *     testid?: string,
 *     disabled?: boolean,
 *     hint?: string  // small caption under the label (e.g. "Sent 3h ago")
 *   }>
 *
 * Designed so each action has a big 56px tap target — "2-tap send" = tap row,
 * tap the giant green "Send via email" button. No more dropdown chains.
 */
export default function MobileQuickActions({
  open,
  onClose,
  title,
  subtitle,
  actions = [],
}) {
  return (
    <Drawer open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DrawerContent
        className="max-h-[85vh] lg:hidden"
        data-testid="mobile-quick-actions-sheet"
      >
        <DrawerHeader className="text-left">
          <DrawerTitle className="truncate">{title}</DrawerTitle>
          {subtitle && (
            <DrawerDescription className="text-xs">{subtitle}</DrawerDescription>
          )}
        </DrawerHeader>

        <div className="px-4 pb-6 space-y-2 overflow-y-auto" data-testid="mobile-quick-actions-list">
          {actions.map((a, i) => {
            const Icon = a.icon;
            return (
              <Button
                key={a.testid || `${a.label}-${i}`}
                onClick={() => {
                  a.onClick?.();
                  onClose?.();
                }}
                disabled={a.disabled}
                variant={a.variant === 'destructive' ? 'destructive' : a.variant === 'ghost' ? 'ghost' : 'outline'}
                data-testid={a.testid || `quick-action-${a.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={cn(
                  'w-full justify-start gap-3 h-14 text-base font-medium',
                  a.variant === 'primary' && 'bg-emerald-500 hover:bg-emerald-600 text-white border-transparent',
                )}
              >
                {Icon && <Icon className="w-5 h-5 flex-shrink-0" />}
                <div className="flex-1 text-left">
                  <div>{a.label}</div>
                  {a.hint && (
                    <div className="text-[11px] text-muted-foreground font-normal mt-0.5">
                      {a.hint}
                    </div>
                  )}
                </div>
              </Button>
            );
          })}
        </div>
      </DrawerContent>
    </Drawer>
  );
}
